import { useState, useRef, useEffect, type JSX } from 'react'
import { useNavigate } from 'react-router'
import { FileCheck, ChevronRight, CheckCircle2, XCircle, Clock } from 'lucide-react'
import { useClickOutside } from '../../hooks/useClickOutside.js'
import { useApprovalCountsStore } from '../../store/approvalCountsStore.js'

export default function ApprovalDropdownWidget(): JSX.Element {
  const [open, setOpen] = useState(false)
  const counts = useApprovalCountsStore((s) => s.counts)
  const refresh = useApprovalCountsStore((s) => s.refresh)
  const [loading, setLoading] = useState(true)
  const ref = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()
  useClickOutside(ref, () => setOpen(false), open)

  // Counts live in the shared store so a decision made elsewhere (ReviewDrawer)
  // updates this badge in real time. We re-sync on open to stay authoritative.
  useEffect(() => {
    let cancelled = false
    setLoading(true)
    void refresh().finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [open, refresh])

  const totalPending = counts.pending + counts.requestedPending

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        className="relative flex h-8 w-8 items-center justify-center rounded-lg text-gray-500 transition-colors hover:bg-gray-100"
        aria-label={`${totalPending} pending approval${totalPending === 1 ? '' : 's'}`}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <FileCheck className="h-4 w-4" aria-hidden />
        {totalPending > 0 && (
          <span className="absolute -right-1 -top-1 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-amber-500 px-1 text-[10px] font-bold text-white">
            {totalPending}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 z-50 mt-1 w-72 rounded-xl border border-gray-200 bg-white py-1 shadow-lg">
          {loading ? (
            <div className="p-4 text-xs text-gray-400">Loading approvals…</div>
          ) : totalPending === 0 && counts.requested === 0 ? (
            <div className="p-4 text-center">
              <FileCheck className="mx-auto mb-2 h-8 w-8 text-gray-300" />
              <p className="text-sm font-medium text-gray-600">No approvals</p>
              <p className="text-xs text-gray-400">You're all caught up.</p>
            </div>
          ) : (
            <>
              {/* For My Approval section */}
              <div className="px-3 py-2">
                <div className="mb-1.5 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-gray-400">
                  <FileCheck className="h-3 w-3" />
                  For My Approval
                </div>
                <button
                  onClick={() => { setOpen(false); navigate('/agent/conversation/approvals') }}
                  className="flex w-full items-center gap-2 rounded-lg px-2 py-2 text-left text-sm hover:bg-gray-50"
                >
                  <div className="flex flex-1 flex-col gap-1">
                    <div className="flex items-center gap-2">
                      <span className="flex items-center gap-1 text-xs font-medium text-gray-700">
                        <Clock className="h-3 w-3 text-amber-500" />
                        Pending: <span className="font-bold text-amber-600">{counts.pending}</span>
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="flex items-center gap-1 text-xs text-gray-500">
                        <CheckCircle2 className="h-3 w-3 text-emerald-500" />
                        {counts.approved}
                      </span>
                      <span className="flex items-center gap-1 text-xs text-gray-500">
                        <XCircle className="h-3 w-3 text-red-500" />
                        {counts.rejected}
                      </span>
                    </div>
                  </div>
                  <ChevronRight className="h-4 w-4 text-gray-300" />
                </button>
              </div>

              <div className="mx-3 border-t border-gray-100" />

              {/* Requested section */}
              <div className="px-3 py-2">
                <div className="mb-1.5 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-gray-400">
                  <Clock className="h-3 w-3" />
                  Requested Approvals
                </div>
                <button
                  onClick={() => { setOpen(false); navigate('/agent/conversation/approvals?tab=requested') }}
                  className="flex w-full items-center gap-2 rounded-lg px-2 py-2 text-left text-sm hover:bg-gray-50"
                >
                  <div className="flex flex-1 flex-col gap-1">
                    <div className="flex items-center gap-2">
                      <span className="flex items-center gap-1 text-xs font-medium text-gray-700">
                        <Clock className="h-3 w-3 text-amber-500" />
                        Pending: <span className="font-bold text-amber-600">{counts.requestedPending}</span>
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="flex items-center gap-1 text-xs text-gray-500">
                        <CheckCircle2 className="h-3 w-3 text-emerald-500" />
                        {counts.requestedApproved}
                      </span>
                      <span className="flex items-center gap-1 text-xs text-gray-500">
                        <XCircle className="h-3 w-3 text-red-500" />
                        {counts.requestedRejected}
                      </span>
                    </div>
                  </div>
                  <ChevronRight className="h-4 w-4 text-gray-300" />
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  )
}
